import QRCode from "react-qr-code"
import { CopyToClipboard } from 'react-copy-to-clipboard'

import { useState, useEffect, useContext, useMemo, useRef } from 'react'
import { useLocation, useParams, Link, useNavigate } from 'react-router-dom'
import { Button, Typography } from '@mui/material'
import { enqueueSnackbar } from 'notistack'

import ContentCopyIcon from '@mui/icons-material/ContentCopy'

import PageWrapper from '/components/page-wrapper'
import { AppContext } from '/providers/app-context'
import HelpText from '/help/pass-installer'
import Menu from '/components/passes/pass-nav-menu'

import { getPass } from '/providers/passes-api'
import StyledPaper from "../components/paper.jsx"

import appleButton from '/assets/images/add-to-apple-wallet.svg'
import googleButton from '/assets/images/add-to-google-wallet.svg'

const PassInstaller = () => {

  const navigate = useNavigate()

  const { templates } = useContext(AppContext)
  const { templateId, passId } = useParams()
  const { apiSource, apiKey, setPasses } = useContext(AppContext)

  const location = useLocation()

  const [pass, setPass] = useState(location.state?.pass)

  const template = useMemo(() => { return templates.filter(t => t.template_id === templateId)[0] }, [templates, templateId])

  useEffect(() => {
    getPassData()
  }, [templateId, passId])

  const getPassData = async () => {
    const p = await getPass({ apiSource, apiKey, passId })
    if (p.ok === false || !p.pass) return navigate("/templates")
    setPasses(current => {
      const idx = current.findIndex(item => item.template_id === templateId && item.pass_id === passId)
      if (idx < 0) return [...current, p.pass]
      const next = [...current]
      next[idx] = p.pass
      return next
    })
    setPass(p.pass)
  }

  return (
    <PageWrapper title="Pass Messages" HelpText={HelpText} heading={`${pass?.pass_id} ${pass?.mode === "test" ? " [test]" : ""}`} menu={<Menu pass={pass} template={template} />}>
      <div className='overflow-auto h-full pb-10'>
        <Content pass={pass} />
      </div>
    </PageWrapper>
  )
}

export default PassInstaller

const Content = ({ pass }) => {

  const { copyingEmail, setCopyingEmail } = useContext(AppContext)
  const copyContainerRef = useRef(null)

  const [emailBarcodes, setEmailBarcodes] = useState(false)
  const [copyButtonsReady, setCopyButtonsReady] = useState(false)
  const [copyBarcodesReady, setCopyBarcodesReady] = useState(false)
  const [copyButtonImages, setCopyButtonImages] = useState({
    apple: appleButton,
    google: googleButton
  })
  const [copyBarcodeImages, setCopyBarcodeImages] = useState({
    apple: null,
    google: null
  })

  useEffect(() => {
    // Safari mail paste is unreliable with external SVG <img> references.
    // Convert wallet SVG buttons to PNG data URLs once and use those for copy payloads.
    const convertSvgToPngDataUrl = async (src, width = 160) => {
      try {
        const image = new Image()
        image.decoding = "async"

        const loaded = await new Promise((resolve, reject) => {
          image.onload = () => resolve(image)
          image.onerror = reject
          image.src = src
        })

        const ratio = loaded.height / loaded.width
        const canvas = document.createElement("canvas")
        canvas.width = width
        canvas.height = Math.round(width * ratio)
        const ctx = canvas.getContext("2d")
        ctx.drawImage(loaded, 0, 0, canvas.width, canvas.height)
        return canvas.toDataURL("image/png")
      }
      catch {
        return src
      }
    }

    const prepare = async () => {
      const [applePng, googlePng] = await Promise.all([
        convertSvgToPngDataUrl(appleButton),
        convertSvgToPngDataUrl(googleButton)
      ])
      setCopyButtonImages({
        apple: applePng,
        google: googlePng
      })
      setCopyButtonsReady(true)
    }

    prepare()
  }, [])

  useEffect(() => {
    if (!pass?.apple_url || !pass?.google_url) {
      setCopyBarcodesReady(false)
      return
    }

    const makeQrPng = async value => {
      try {
        const svg = copyContainerRef.current?.querySelector(`svg[data-qr-value="${value}"]`)
        if (!svg) return null

        const serializer = new XMLSerializer()
        const svgString = serializer.serializeToString(svg)
        const svgBlob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" })
        const svgUrl = URL.createObjectURL(svgBlob)

        const image = new Image()
        image.decoding = "async"
        const loaded = await new Promise((resolve, reject) => {
          image.onload = () => resolve(image)
          image.onerror = reject
          image.src = svgUrl
        })

        const canvas = document.createElement("canvas")
        canvas.width = 154
        canvas.height = 154
        const ctx = canvas.getContext("2d")
        ctx.fillStyle = "#ffffff"
        ctx.fillRect(0, 0, canvas.width, canvas.height)
        ctx.drawImage(loaded, 0, 0, canvas.width, canvas.height)

        URL.revokeObjectURL(svgUrl)
        return canvas.toDataURL("image/png")
      }
      catch {
        return null
      }
    }

    const prepare = async () => {
      await new Promise(resolve => requestAnimationFrame(resolve))
      const [appleQr, googleQr] = await Promise.all([
        makeQrPng(pass.apple_url),
        makeQrPng(pass.google_url)
      ])

      setCopyBarcodeImages({
        apple: appleQr,
        google: googleQr
      })
      setCopyBarcodesReady(Boolean(appleQr && googleQr))
    }

    if (copyingEmail && emailBarcodes) prepare()
  }, [copyingEmail, emailBarcodes, pass?.apple_url, pass?.google_url])

  useEffect(() => {
    if (!copyingEmail) return
    if (!copyButtonsReady) return
    if (emailBarcodes && !copyBarcodesReady) return

    const fallbackExecCommandCopy = node => {
      if (!node) return false
      const selection = window.getSelection()
      const range = document.createRange()
      range.selectNodeContents(node)
      selection.removeAllRanges()
      selection.addRange(range)
      const copied = document.execCommand("copy")
      selection.removeAllRanges()
      return copied
    }

    const copy = async () => {
      // Wait one paint so the copy-only DOM has rendered the prepared PNG button sources.
      await new Promise(resolve => requestAnimationFrame(resolve))

      const node = copyContainerRef.current
      if (!node) return

      const html = node.innerHTML
      const text = [
        `Apple Wallet: ${pass?.apple_url ?? ""}`,
        `Google Wallet: ${pass?.google_url ?? ""}`
      ].join("\n")
      let copied = false

      try {
        // Primary path: explicit HTML + plain text clipboard payload.
        // This is more reliable than execCommand in modern Safari/Chrome.
        if (navigator.clipboard && window.ClipboardItem) {
          const item = new ClipboardItem({
            "text/html": new Blob([html], { type: "text/html" }),
            "text/plain": new Blob([text], { type: "text/plain" })
          })
          await navigator.clipboard.write([item])
          copied = true
        }
      }
      catch (err) {
        console.error("Clipboard API copy failed, falling back to execCommand", err)
      }

      // Fallback path for environments where Clipboard API write is blocked/unavailable.
      if (!copied) copied = fallbackExecCommandCopy(node)

      enqueueSnackbar({
        message: copied ? "Email content copied" : "Unable to copy email content",
        variant: copied ? "success" : "error"
      })

      setCopyingEmail(false)
      setEmailBarcodes(false)
    }

    copy()
  }, [copyingEmail, copyButtonsReady, copyBarcodesReady, emailBarcodes, pass?.apple_url, pass?.google_url])

  const copyEmail = () => {
    setCopyingEmail(true)
  }

  const copyEmailBarcodes = () => {
    setEmailBarcodes(true)
    setCopyingEmail(true)
  }

  if (copyingEmail) return (
    <div ref={copyContainerRef} style={{ backgroundColor: "#fff", width: "100%" }}>
      <table cellPadding={emailBarcodes ? 6 : 8} cellSpacing={emailBarcodes ? 6 : 8}>
        <tbody>
          <tr style={{ height: emailBarcodes ? 52 : 50 }}>
            <td style={{ textAlign: "center" }}><a href={pass?.apple_url} target='_blank' rel="noreferrer"><img src={copyButtonImages.apple} width={160} style={{ display: "block", margin: "0 auto" }} /></a></td>
            <td style={{ textAlign: "center" }}><a href={pass?.google_url} target='_blank' rel="noreferrer"><img src={copyButtonImages.google} width={160} style={{ display: "block", margin: "0 auto" }} /></a></td>
          </tr>
          {emailBarcodes &&
            <tr style={{ height: 160, width: 160 }}>
              <td style={{ textAlign: "center" }}>
                <div className='bg-white' style={{ width: 160, margin: "0 auto", padding: 3 }}>
                  {copyBarcodeImages.apple
                    ? <img src={copyBarcodeImages.apple} width={154} height={154} style={{ display: "block", margin: "0 auto" }} />
                    : <QRCode
                      size={154}
                      style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                      value={pass?.apple_url}
                      viewBox={`0 0 256 256`}
                    />}
                </div>
              </td>

              <td style={{ textAlign: "center" }}>
                <div className='bg-white' style={{ width: 160, margin: "0 auto", padding: 3 }}>
                  {copyBarcodeImages.google
                    ? <img src={copyBarcodeImages.google} width={154} height={154} style={{ display: "block", margin: "0 auto" }} />
                    : <QRCode
                      size={154}
                      style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                      value={pass?.google_url}
                      viewBox={`0 0 256 256`} />}
                </div>
              </td>
            </tr>
          }
        </tbody>
      </table>
      {emailBarcodes && <div style={{ position: "absolute", left: -9999, top: 0 }}>
        <QRCode data-qr-value={pass?.apple_url} size={256} value={pass?.apple_url} viewBox={`0 0 256 256`} />
        <QRCode data-qr-value={pass?.google_url} size={256} value={pass?.google_url} viewBox={`0 0 256 256`} />
      </div>}
      <br /><br />
    </div>
  )


  if (pass) return (
    <div className='flex flex-row gap-40 items-center m-10 justify-around'>

      <StyledPaper>
        <div className='flex flex-row gap-20 p-5'>
          <div className='flex flex-col gap-10 items-start'>
            <div style={{ height: 100 }} className="flex flex-col justify-around">
              <Link to={pass.apple_url} target='_pass'><img src={appleButton} width={250} /></Link>
            </div>

            <div style={{ height: 250, width: 250 }} className='bg-white p-5' >
              <QRCode
                size={256}
                style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                value={pass.apple_url}
                viewBox={`0 0 256 256`}
              />
            </div>

            <CopyToClipboard text={pass.apple_url} onCopy={() => enqueueSnackbar({ message: 'Apple install URL copied', variant: 'success' })}>
              <Button><ContentCopyIcon color='inherit' className="mr-5" />Copy URL to clipboard</Button>
            </CopyToClipboard>
          </div>

          <div className='flex flex-col gap-10 items-start'>
            <div style={{ height: 100 }} className="flex flex-col justify-around">
              <Link to={pass.google_url} target='_pass'><img src={googleButton} width={250} /></Link>
            </div>

            <div style={{ height: 250, width: 250 }} className='bg-white p-5'>
              <QRCode
                size={256}
                style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                value={pass.google_url}
                viewBox={`0 0 256 256`}
              />
            </div>

            <CopyToClipboard text={pass.google_url} onCopy={() => enqueueSnackbar({ message: 'Google install URL copied', variant: 'success' })}>
              <Button><ContentCopyIcon color='inherit' className="mr-5" />Copy URL to clipboard</Button>
            </CopyToClipboard>
          </div>
        </div>
      </StyledPaper>

      <StyledPaper>
        <div className='flex flex-col gap-5 p-5'>
          <Typography variant='h6'>Pass ID:
            <CopyToClipboard text={pass.pass_id} onCopy={() => enqueueSnackbar({ message: 'Gift card ID copied', variant: 'success' })} options={{ format: 'text/plain' }}>
              <Button ><ContentCopyIcon color='inherit' className="mr-5" />{pass.pass_id}</Button>
            </CopyToClipboard>
          </Typography>

          <Button variant='contained' color="secondary" onClick={copyEmailBarcodes}><ContentCopyIcon color='inherit' className="mr-5" />Copy email content (with barcodes)</Button>
          <Button variant='contained' color="secondary" onClick={copyEmail}><ContentCopyIcon color='inherit' className="mr-5" />Copy email content (no barcodes)</Button>
        </div>
      </StyledPaper>
    </div>
  )
}
